<template>
  <InputNumber
    :modelValue="modelValue"
    :inputId="inputId"
    :placeholder="placeholder"
    :disabled="disabled"
    :min="min"
    :max="max"
    :minFractionDigits="minFractionDigits"
    :maxFractionDigits="maxFractionDigits"
    mode="decimal"
    :locale="locale"
    :useGrouping="useGrouping"
    :class="[inputClass, { 'p-invalid': invalid }]"
    :inputProps="{ onKeydown: onDecimalKeydown }"
    @update:modelValue="emit('update:modelValue', $event)"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  />
</template>

<script setup lang="ts">
import InputNumber from "primevue/inputnumber";

/**
 * PrimeVue InputNumber accepts only the locale decimal separator.
 * This wrapper also accepts the alternate character (`.` ↔ `,`) on keydown.
 * @see https://github.com/primefaces/primevue/issues/8012
 */
const props = withDefaults(
  defineProps<{
    modelValue?: number | null;
    inputId?: string;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    min?: number;
    max?: number;
    minFractionDigits?: number;
    maxFractionDigits?: number;
    locale?: string;
    useGrouping?: boolean;
    inputClass?: string;
  }>(),
  {
    modelValue: null,
    invalid: false,
    minFractionDigits: 0,
    maxFractionDigits: 2,
    locale: "ru-RU",
    useGrouping: false,
    inputClass: "w-full",
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: number | null];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const decimalChar =
  new Intl.NumberFormat(props.locale)
    .formatToParts(1.1)
    .find((part) => part.type === "decimal")?.value ?? ",";

const alternateDecimalChar = decimalChar === "," ? "." : ",";

function onDecimalKeydown(event: KeyboardEvent) {
  if (event.key !== alternateDecimalChar) return;
  event.preventDefault();
  const input = event.target as HTMLInputElement | null;
  if (!input) return;
  input.dispatchEvent(
    new KeyboardEvent("keypress", {
      key: decimalChar,
      bubbles: true,
      cancelable: true,
    })
  );
}
</script>
